import React, { useState, useEffect } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { Box, Select, MenuItem, Typography, FormControl, Pagination, PaginationItem } from '@mui/material';
import { GridFooterContainer } from '@mui/x-data-grid';
import '../components/DeviceTable.css';
import { useTranslation } from 'react-i18next';
import CustomerDeviceInfoDialog from './CustomerDeviceInfoDialog';
import { styled } from '@mui/system';
import koKR from '../components/koKR.json'; // Import the translation file
import CustomColumnSortedAscendingIcon from '../components/CustomColumnSortedAscendingIcon';
import CustomColumnSortedDescendingIcon from '../components/CustomColumnSortedDescendingIcon';
import apiClient from '../api/apiClient'; // API client import
import Config from '../Config'; // apiUrl 추가
import { getAccessToken } from '../utils/token';

const apiUrl = Config.apiUrl;

function CustomPagination(props) {
    const pageCount = Math.ceil(props.totalElements / props.size);
    const rowsPerPage = props.size;

    const handlePageChange = (event, value) => {
        props.onPageChange(value - 1); // DataGrid는 0-based page, Pagination은 1-based page
    };

    const handlePageSizeChange = (event) => {
        props.onPageSizeChange(parseInt(event.target.value, 10));
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', width: '100%', marginBottom: '25px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1">Showing</Typography>
                <FormControl variant="outlined" sx={{ mx: 1, minWidth: 78, width: 78, height: 38 }}>
                    <Select value={rowsPerPage} onChange={handlePageSizeChange}
                        disableUnderline
                        sx={{ fontSize: '0.875rem', height: '100%', paddingTop: '8px', paddingBottom: '8px' }}>
                        {[10, 20, 30, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body1">of {props.totalElements}</Typography>
            </Box>
            {props.totalElements > 0 && (
                <Pagination
                    count={pageCount}
                    page={props.page + 1}
                    onChange={handlePageChange}
                    siblingCount={1}
                    boundaryCount={1}
                    shape="rounded"
                    color="primary"
                    variant='outlined'
                    renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            sx={{
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                },
                                backgroundColor: item.selected ? 'initial' : '#e0e0e0',
                                color: item.selected ? '#fff' : '#8b909a', // 선택되지 않았을 때 글자색 설정
                                border: 'none',
                                width: '28px',    // 가로 크기 설정
                                height: '28px',   // 세로 크기 설정
                                margin: '1px',    // 박스 간 간격 설정 (전체 간격이 2px이므로 양쪽에 1px씩)
                            }}
                        />
                    )}
                />
            )}
        </Box>
    );
}

const NoRowsOverlay = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    fontSize: '1.5rem',
    color: '#888',
    flexDirection: 'column',
});

function CustomFooter(props) {
    return (
        <GridFooterContainer sx={{ borderTop: 'none', color: '#8b909a' }}>
            <CustomPagination
                page={props.page}
                size={props.size}
                totalElements={props.totalElements}
                onPageChange={props.onPageChange}
                onPageSizeChange={props.onPageSizeChange}
            />
        </GridFooterContainer>
    );
}

const CustomerDeviceList = () => {
    const [rows, setRows] = useState([]);
    const apiRef = useGridApiRef();
    const { i18n } = useTranslation();
    const { t } = useTranslation('console');
    const getRowHeight = (params) => 58;
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Pagination and sorting states
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0); // 0-based page index
    const [size, setSize] = useState(10); // Page size
    const [sort, setSort] = useState(['createdAt,desc']); // Default sort
    const [totalElements, setTotalElements] = useState(0); // Total number of elements for pagination

    const handleRowClick = (params) => {
        setSelectedRowId(params.id);  // 선택된 행의 ID를 저장
        setDialogOpen(true);  // 다이얼로그 열기
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const columns = [
        { field: 'id', headerName: `${t('customer_device_list.column.id')}`, flex: 1, minWidth: 70, headerAlign: 'center', align: 'center' },
        { field: 'country', headerName: `${t('customer_device_list.column.country')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'region', headerName: `${t('customer_device_list.column.region')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'reseller', headerName: `${t('customer_device_list.column.reseller')}`, flex: 2, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'manager', headerName: `${t('customer_device_list.column.manager')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'model', headerName: `${t('customer_device_list.column.model')}`, flex: 2, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'customer', headerName: `${t('customer_device_list.column.customer')}`, flex: 2, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'serial', headerName: `${t('customer_device_list.column.serial')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'production_date', headerName: `${t('customer_device_list.column.production_date')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'connection_state', headerName: `${t('customer_device_list.column.connection_state')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center', renderCell: (params) => renderConnection(params) }
    ];

    // API 호출 및 데이터 업데이트
    const fetchCustomerDevices = async () => {
        setLoading(true);
        try {
            const token = getAccessToken();
            const response = await apiClient.get(`${apiUrl}/console/customer-devices`, {
                headers: {
                    Authorization: `Bearer ${token}`, // Bearer 토큰 추가
                },
                params: {
                    page: page,
                    size: size,
                    sort: sort, // 정렬 파라미터 (배열 그대로 전달)
                },
            });

            const updatedRows = response.data.content.map((device) => ({
                id: device.id, // 'id' 필드 매핑
                country: device.country, // 'country' 필드 매핑
                region: device.area, // 'region'은 'area' 필드로 매핑
                reseller: device.reseller, // 'reseller' 필드 매핑
                manager: device.manager, // 'manager' 필드 매핑
                model: device.prodName, // 'model'은 'prodName' 필드로 매핑
                customer: device.email, // 'customer'은 'email' 필드로 매핑
                serial: device.serial, // 'serial' 필드 매핑
                production_date: device.productAt, // 'production_date'는 'productAt' 필드로 매핑
                connection_state: device.isConnect // 'connection_state'는 'isConnect' 필드로 매핑
            }));

            setRows(updatedRows); // rows 상태 업데이트
            setTotalElements(response.data.totalElements); // 총 데이터 수 설정
        } catch (error) {
            console.error('Error fetching device data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomerDevices(); // 컴포넌트가 마운트되었을 때 데이터 호출
    }, [page, size, sort]); // 페이지, 사이즈, 정렬 변경 시 데이터 다시 호출

    function renderConnection(params) {
        console.log(params);
        return (
            <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {params.value ? <img src="online.png" alt="online" /> : <img src="offline.png" alt="offline" />}
            </Box>
        );
    }

    const getLocaleText = () => {
        return i18n.language === 'ko' ? koKR : {};
    };

    return (
        <Box sx={{ width: '1592px', minWidth: 1024 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontSize: '32px', color: '#002a70', fontWeight: 'bold', mt: '20px' }}>
                    {t('customer_device_list.header.title')}
                </Typography>
            </Box>
            <Box sx={{ width: '1524px', height: '802px', mt: '41px' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    getRowHeight={getRowHeight}
                    onRowClick={handleRowClick}
                    headerHeight={48}
                    localeText={getLocaleText()}
                    loading={loading} // DataGrid의 loading prop을 활성화
                    rowCount={totalElements}
                    pageSizeOptions={[10, 20, 30, 50]}
                    paginationModel={{ page, pageSize: size }}
                    onPaginationModelChange={(newModel) => {
                        setPage(newModel.page);
                        setSize(newModel.pageSize);
                    }}
                    sortingMode="server"
                    sortModel={sort.map(s => ({ field: s.split(',')[0], sort: s.split(',')[1] }))}
                    onSortModelChange={(newSortModel) => {
                        setSort(newSortModel.map(s => `${s.field},${s.sort}`));
                    }}
                    paginationMode="server"
                    slots={{
                        footer: () => (
                            <CustomFooter
                                page={page}
                                size={size}
                                totalElements={totalElements}
                                onPageChange={setPage}
                                onPageSizeChange={setSize}
                            />
                        ),
                        noRowsOverlay: () => (loading ? null : ( // 로딩 중일 때는 noRowsOverlay를 숨김
                            <NoRowsOverlay>
                                <img src="nodata.png" alt="No data" />
                            </NoRowsOverlay>
                        )),
                        columnSortedAscendingIcon: CustomColumnSortedAscendingIcon,
                        columnSortedDescendingIcon: CustomColumnSortedDescendingIcon,
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    sx={{
                        '& .MuiDataGrid-columnHeaders div[role="row"]': {
                            backgroundColor: '#F5F5F7',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: '800',
                            color: '#7d7d7d',
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontSize: '16px',
                            color: '#494949',
                            height: '58px',
                            backgroundColor: '#ffffff',
                        },
                        '& .MuiDataGrid-cellContent': {
                            width: '100%',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '95px',
                            backgroundColor: '#ffffff',
                        },
                        borderRadius: '14px',
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                    }}
                />
                {/* 다이얼로그 컴포넌트 */}
                <CustomerDeviceInfoDialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    rowId={selectedRowId}
                />
            </Box>
        </Box>
    );
};

export default CustomerDeviceList;